import { createContext, useCallback, useContext, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, api, type Me } from "@/api/client";

interface AuthValue {
  me: Me | null;
  isLoading: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  acceptInvite: (token: string, password: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<Me | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.get<Me>("/api/auth/me");
      } catch (error) {
        // A 401 here is the normal "not signed in" case, not a failure — it
        // must not surface as an error state or retry.
        if (error instanceof ApiError && error.isUnauthenticated) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 30_000,
  });

  const login = useCallback(
    async (email: string, password: string) => {
      const me = await api.post<Me>("/api/auth/login", { email, password });
      queryClient.setQueryData(["me"], me);
    },
    [queryClient],
  );

  const acceptInvite = useCallback(
    async (token: string, password: string) => {
      // Same shape as login: the endpoint sets the session cookie and returns
      // the new account, so it slots into the same cache update (§6.2 step 4).
      const me = await api.post<Me>("/api/auth/accept-invite", { token, password });
      queryClient.setQueryData(["me"], me);
    },
    [queryClient],
  );

  const resetPassword = useCallback(
    async (token: string, password: string) => {
      // Same shape as acceptInvite — "identical machinery" (§6.2 step 5).
      const me = await api.post<Me>("/api/auth/reset-password", { token, password });
      queryClient.setQueryData(["me"], me);
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } finally {
      // Clear local state even if the call failed, so the UI cannot be left
      // showing a signed-in shell for a session the server has dropped.
      queryClient.setQueryData(["me"], null);
      queryClient.clear();
    }
  }, [queryClient]);

  const value = useMemo<AuthValue>(
    () => ({
      me: data ?? null,
      isLoading,
      isAdmin: data?.user.system_role === "admin",
      login,
      acceptInvite,
      resetPassword,
      logout,
    }),
    [data, isLoading, login, acceptInvite, resetPassword, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
